// ABOUTME: Loads and persists a user's full column state (order + visibility) for a data table
// ABOUTME: Server-side per-user storage, used by tables that persist column order, not only visibility

import type { ColumnState } from '@ds/data-table';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = '/api/settings/user/table-column-states';

// Only the fields that describe the layout are stored; `sortIndex` (the active sort) is
// re-added as null on read so the table receives a full ColumnState.
type PersistedColumnState = Pick<ColumnState, 'columnName' | 'index' | 'isActive'>;

// Toggling or reordering a column makes the table rebuild its cells; the write waits long
// enough to stay out of that window and to coalesce a burst of changes into a single
// request. Pending writes are flushed when the table unmounts or the page is left.
const SAVE_DEBOUNCE_MS = 2000;

type UserTableColumnStates = Record<string, PersistedColumnState[]>;

// Shared across every table on the page: the preferences of all tables live in a single
// user settings document, so one request serves all mounted tables.
let statesCache: UserTableColumnStates | null = null;
let pendingRequest: Promise<UserTableColumnStates> | null = null;

const loadUserTableColumnStates = (): Promise<UserTableColumnStates> => {
    if (statesCache) {
        return Promise.resolve(statesCache);
    }

    if (!pendingRequest) {
        pendingRequest = fetch(API_URL, { credentials: 'include' })
            .then(async response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch table column states');
                }
                const result = await response.json();
                return (result.data ?? {}) as UserTableColumnStates;
            })
            .then(data => {
                statesCache = data;
                pendingRequest = null;
                return data;
            })
            .catch(error => {
                pendingRequest = null;
                throw error;
            });
    }

    return pendingRequest;
};

const saveTableColumnState = async (tableId: string, columnState: PersistedColumnState[]) => {
    if (statesCache) {
        statesCache[tableId] = columnState;
    }

    await fetch(API_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Lets the request survive the page being left, so a flushed write is not dropped.
        keepalive: true,
        body: JSON.stringify({ tableId, columnState }),
    });
};

const toColumnState = (stored: PersistedColumnState[]): ColumnState[] =>
    stored.map(column => ({ ...column, sortIndex: null }));

const toPersisted = (columnState: ColumnState[]): PersistedColumnState[] =>
    columnState.map(({ columnName, index, isActive }) => ({ columnName, index, isActive }));

type UseTableColumnStateResult = {
    /** State to hand to the table as its initial column state, undefined when nothing is saved. */
    columnState: ColumnState[] | undefined;
    /** False until the saved state is known; the table must not mount before then. */
    loaded: boolean;
    /** Persists the column state of the table, debounced and skipping no-op writes. */
    save: (columnState: ColumnState[]) => void;
};

/**
 * Per-user full column state (order + visibility) of a single table, stored in the user
 * settings document.
 *
 * The table reads its column state once, when mounting, so `loaded` gates the render:
 * a value arriving later would be ignored by the table.
 */
export const useTableColumnState = (
    tableId: string | undefined,
    enabled: boolean,
): UseTableColumnStateResult => {
    const isActive = !!tableId && enabled;

    const [columnState, setColumnState] = useState<ColumnState[] | undefined>();
    const [loaded, setLoaded] = useState(!isActive);

    // Last persisted value, to skip writes that would not change anything.
    const savedRef = useRef<string | undefined>(undefined);
    // Value currently exposed as `columnState`, to skip state updates that change nothing.
    const exposedRef = useRef<string | undefined>(undefined);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    // State waiting to be written, kept so it can be flushed early.
    const pendingRef = useRef<PersistedColumnState[] | undefined>(undefined);

    useEffect(() => {
        if (!isActive || !tableId) {
            setLoaded(true);
            return undefined;
        }

        let cancelled = false;

        loadUserTableColumnStates()
            .then(states => {
                if (cancelled) return;
                const stored = states[tableId];
                setColumnState(stored ? toColumnState(stored) : undefined);
                savedRef.current = stored ? JSON.stringify(stored) : undefined;
                exposedRef.current = savedRef.current;
                setLoaded(true);
            })
            .catch(() => {
                if (cancelled) return;
                setColumnState(undefined);
                setLoaded(true);
            });

        return () => {
            cancelled = true;
        };
    }, [isActive, tableId]);

    const flush = useCallback(() => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        const next = pendingRef.current;
        pendingRef.current = undefined;
        if (!tableId || !next) return;

        const serialized = JSON.stringify(next);
        if (serialized === savedRef.current) return;
        savedRef.current = serialized;

        saveTableColumnState(tableId, next).catch(() => undefined);
    }, [tableId]);

    // Kept up to date on every render so the unmount and pagehide handlers, registered
    // once, never call a stale flush.
    const flushRef = useRef(flush);
    flushRef.current = flush;

    useEffect(() => {
        const onPageHide = () => flushRef.current();
        window.addEventListener('pagehide', onPageHide);

        return () => {
            window.removeEventListener('pagehide', onPageHide);
            flushRef.current();
        };
    }, []);

    const save = useCallback(
        (next: ColumnState[]) => {
            if (!isActive || !tableId) return;

            const persisted = toPersisted(next);
            pendingRef.current = persisted;

            // The exposed value is what the table receives as its initial column state, and
            // the column manager re-reads it every time it remounts (a page change, a column
            // filter, a sort). Refreshing it here keeps a remount from restoring the layout
            // the user has just replaced. Guarded on the serialized form because a drag
            // reorder fires this once per hover step.
            const serialized = JSON.stringify(persisted);
            if (serialized !== exposedRef.current) {
                exposedRef.current = serialized;
                setColumnState(toColumnState(persisted));
            }

            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(() => flushRef.current(), SAVE_DEBOUNCE_MS);
        },
        [isActive, tableId],
    );

    return { columnState, loaded, save };
};
